import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CheckCircle, Clock, Loader2, User } from 'lucide-react';
import { usePayrollApproval } from '@/hooks/usePayrollApproval';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import { th } from 'date-fns/locale';

interface PayrollApprovalStatusProps {
  month: number;
  year: number;
}

export function PayrollApprovalStatus({ month, year }: PayrollApprovalStatusProps) {
  const {
    approvalStatus,
    userRole,
    isLoading,
    canApproveStep1,
    canApproveStep2,
    approveManagement,
    approveAccounting,
    isApprovingManagement,
    isApprovingAccounting,
  } = usePayrollApproval(month, year);

  // Fetch approver names
  const { data: approverNames } = useQuery({
    queryKey: ['approver-names', approvalStatus?.management_approved_by, approvalStatus?.accounting_approved_by],
    queryFn: async () => {
      const userIds = [
        approvalStatus?.management_approved_by,
        approvalStatus?.accounting_approved_by,
      ].filter(Boolean);

      if (userIds.length === 0) return {};

      const { data, error } = await supabase
        .from('profiles')
        .select('id, full_name, email')
        .in('id', userIds);

      if (error) {
        console.error('Error fetching approver names:', error);
        return {};
      }

      return data.reduce((acc, profile) => {
        acc[profile.id] = profile.full_name || profile.email;
        return acc;
      }, {} as Record<string, string>);
    },
    enabled: !!approvalStatus?.management_approved_by || !!approvalStatus?.accounting_approved_by,
  });

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-6">
          <Loader2 className="h-6 w-6 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  const step1Approved = approvalStatus?.management_approved || false;
  const step2Approved = approvalStatus?.accounting_approved || false;

  const getOverallStatus = () => {
    if (step2Approved) return { label: 'อนุมัติครบแล้ว', className: 'bg-green-500' };
    if (step1Approved) return { label: 'รอบัญชีอนุมัติ', className: 'bg-blue-500 text-white' };
    return { label: 'รอ Management อนุมัติ', className: 'bg-amber-100 text-amber-800 border-amber-300' };
  };

  const status = getOverallStatus();

  const formatApprovalDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return format(new Date(dateStr), 'dd MMM yyyy HH:mm', { locale: th });
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center justify-between">
          <span className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5" />
            สถานะการอนุมัติเงินเดือน
          </span>
          <Badge className={status.className}>{status.label}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Step 1: Fishsix Management */}
          <div className={`p-4 rounded-lg border ${step1Approved ? 'bg-green-50 border-green-200' : 'bg-muted/50'}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step1Approved ? 'bg-green-500 text-white' : 'bg-muted-foreground/20'}`}>
                  {step1Approved ? <CheckCircle className="h-5 w-5" /> : <Clock className="h-5 w-5" />}
                </div>
                <div>
                  <div className="font-medium">ขั้นที่ 1: Fishsix Management</div>
                  {step1Approved && approvalStatus?.management_approved_at && (
                    <div className="text-sm text-muted-foreground flex items-center gap-1">
                      <User className="h-3 w-3" />
                      {approverNames?.[approvalStatus.management_approved_by!] || 'Unknown'}
                      <span className="mx-1">•</span>
                      {formatApprovalDate(approvalStatus.management_approved_at)}
                    </div>
                  )}
                </div>
              </div>
              {canApproveStep1 && (
                <Button 
                  onClick={() => approveManagement()} 
                  disabled={isApprovingManagement}
                  size="sm"
                >
                  {isApprovingManagement && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  อนุมัติ
                </Button>
              )}
            </div>
          </div>

          {/* Step 2: Accounting */}
          <div className={`p-4 rounded-lg border ${step2Approved ? 'bg-green-50 border-green-200' : step1Approved ? 'bg-blue-50 border-blue-200' : 'bg-muted/30 opacity-60'}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step2Approved ? 'bg-green-500 text-white' : step1Approved ? 'bg-blue-500 text-white' : 'bg-muted-foreground/20'}`}>
                  {step2Approved ? <CheckCircle className="h-5 w-5" /> : <Clock className="h-5 w-5" />}
                </div>
                <div>
                  <div className="font-medium">ขั้นที่ 2: ฝ่ายบัญชี</div>
                  {step2Approved && approvalStatus?.accounting_approved_at && (
                    <div className="text-sm text-muted-foreground flex items-center gap-1">
                      <User className="h-3 w-3" />
                      {approverNames?.[approvalStatus.accounting_approved_by!] || 'Unknown'}
                      <span className="mx-1">•</span>
                      {formatApprovalDate(approvalStatus.accounting_approved_at)}
                    </div>
                  )}
                  {!step1Approved && (
                    <div className="text-sm text-muted-foreground">รอขั้นที่ 1 อนุมัติก่อน</div>
                  )}
                </div>
              </div>
              {canApproveStep2 && (
                <Button 
                  onClick={() => approveAccounting()} 
                  disabled={isApprovingAccounting}
                  size="sm"
                >
                  {isApprovingAccounting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  อนุมัติ
                </Button>
              )}
            </div>
          </div>

          {/* Current user role info */}
          {userRole && (
            <div className="text-xs text-muted-foreground text-center pt-2 border-t">
              สิทธิ์ของคุณ: <span className="font-medium">{userRole === 'fishsix_management' ? 'Fishsix Management' : 'ฝ่ายบัญชี'}</span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
